import { lazy, Suspense } from 'react';
import { Admin, Resource } from 'react-admin';
import Amplify from 'aws-amplify';
import awsmobile from './aws-exports';
// Providers
import themeApp from './providers/themeProvider';
import permissionsProvider from './providers/permissionsProvider';
import dataProvider from './providers/dataProvider';
import authProvider from './providers/authProvider';
import i18nProvider from './providers/i18nProvider';
import routes from './routes';
// Components
import { LoaderApp } from './components/layout/LoaderApp';
import { AppLayout } from './layouts/AppLayout';
import { version } from '../package.json';
import CacheBuster from 'react-cache-buster';
import {lazyRetry} from './utils/index';

const Login = lazy(() => lazyRetry(() => import('./views/auth/Login')));
// Views
const Dashboard = lazy(() => lazyRetry(() => import('./views/dashboard')));
const Settings = lazy(() => lazyRetry(() => import('./views/settings')));
// Processors views
const Processors = lazy(() => lazyRetry(() => import('./views/processors/Processors')));
const CreateProcessor = lazy(() => lazyRetry(() => import('./views/processors/CreateProcessor')));
const EditProcessor = lazy(() => lazyRetry(() => import('./views/processors/EditProcessor')));
// Features views
const Features = lazy(() => lazyRetry(() => import('./views/features/Features')));
const CreateFeature = lazy(() => lazyRetry(() => import('./views/features/CreateFeature')));
const EditFeature = lazy(() => lazyRetry(() => import('./views/features/EditFeature')));
// PaymentMethods views
const PaymentMethods = lazy(() => lazyRetry(() => import('./views/payment_methods/PaymentMethods')));
const CreatePaymentMethods = lazy(() => lazyRetry(() => import('./views/payment_methods/CreatePaymentMethods')));
const EditPaymentMethods = lazy(() => lazyRetry(() => import('./views/payment_methods/EditPaymentMethods')));
// Banks views
const Banks = lazy(() => lazyRetry(() => import('./views/banks/Banks')));
const CreateBank = lazy(() => lazyRetry(() => import('./views/banks/CreateBank')));
const EditBank = lazy(() => lazyRetry(() => import('./views/banks/EditBank')));
// Credentials views
// const Credentials = lazy(() => import('./views/credentials/Credentials'));
// const CreateCredential = lazy(() => import('./views/credentials/CreateCredential'));
// const EditCredential = lazy(() => import('./views/credentials/EditCredential'));
// Business views
const Business = lazy(() => lazyRetry(() => import('./views/business/Business')));
const CreateBusiness = lazy(() => lazyRetry(() => import('./views/business/CreateBusiness')));
const EditBusiness = lazy(() => lazyRetry(() => import('./views/business/EditBusiness')));
// Branches views
const Branches = lazy(() => lazyRetry(() => import('./views/branches/Branches')));
const CreateBranch = lazy(() => lazyRetry(() => import('./views/branches/CreateBranch')));
const EditBranch = lazy(() => lazyRetry(() => import('./views/branches/EditBranch')));
// Users views
const Users = lazy(() => lazyRetry(() => import('./views/users/Users')));
const CreateUser = lazy(() => lazyRetry(() => import('./views/users/CreateUser')));
const EditUser = lazy(() => lazyRetry(() => import('./views/users/EditUser')));
// Terminals views
const Terminals = lazy(() => lazyRetry(() => import('./views/terminals/Terminals')));
const CreateTerminal = lazy(() => lazyRetry(() => import('./views/terminals/CreateTerminal')));
const EditTerminal = lazy(() => lazyRetry(() => import('./views/terminals/EditTerminal')));
// Products views
const Products = lazy(() => lazyRetry(() => import('./views/products/Products')));
const CreateProduct = lazy(() => lazyRetry(() => import('./views/products/CreateProduct')));
const EditProduct = lazy(() => lazyRetry(() => import('./views/products/EditProduct')));
const ProductBatch = lazy(() => lazyRetry(() => import('./views/products/ProductBatch')));
const ProductStock = lazy(() => lazyRetry(() => import('./views/products/ProductStock')));
const DetailProduct = lazy(() => lazyRetry(() => import('./views/products/DetailProduct')));
// Orders views
const Orders = lazy(() => lazyRetry(() => import('./views/order/Orders')));
const OrderDetail = lazy(() => lazyRetry(() => import ('./views/order/OrderDetail')));
// Transactions views
const Transactions = lazy(() => lazyRetry(() => import('./views/transactions/Transactions')));
const TransactionDetail = lazy(() => lazyRetry(() => import('./views/transactions/TransactionDetail')));
// Categories views
const Categories = lazy(() => import('./views/categories/Categories'));
const CreateCategory = lazy(() => import('./views/categories/CreateCategory'));
const EditCategory = lazy(() => import('./views/categories/EditCategory'));
// Reports views
const SoldProductsDetail = lazy(() => lazyRetry(() => import('./views/reports/SoldProductsDetail')));
const SoldProducts = lazy(() => lazyRetry(() => import('./views/reports/SoldProducts')));
const CustomReport = lazy(() => lazyRetry(() => import('./views/reports/CustomReport')));
const CloseReports = lazy(() => lazyRetry(() => import('./views/reports/CloseReports')));
// Services
const Services = lazy(() => lazyRetry(() => import('./views/services/Services')));

const isProduction = process.env.NODE_ENV === 'production';

if (isProduction) {
  awsmobile.aws_cloud_logic_custom[0].endpoint =`/api/${process.env.REACT_APP_API_ID}`
}
Amplify.configure(awsmobile);

//Amplify.Logger.LOG_LEVEL = "DEBUG"
const App = () => (
  <CacheBuster
    currentVersion={version}
    isEnabled={isProduction} //If false, the library is disabled.
    isVerboseMode={false} //If true, the library writes verbose logs to console.
    loadingComponent={<LoaderApp />} //If not pass, nothing appears at the time of new version check.
    metaFileDirectory={'.'} //If public assets are hosted somewhere other than root on your server.
  >
    <Suspense fallback={<LoaderApp />}>
      <Admin disableTelemetry
        layout={AppLayout}
        title="Backoffice"
        theme={themeApp}
        customRoutes={routes}
        i18nProvider={i18nProvider}
        dataProvider={dataProvider}
        authProvider={authProvider}
        loginPage={Login}
      >
        {(permissions) => {
          let role = permissionsProvider[permissions]
          if (permissions === 'guest') {
            return [[]];
          } else {
            return [
              role['Dashboard'] !== undefined
                ? <Resource name="/" list={Dashboard} />
                : null,
              role['Products'] !== undefined
                ? <Resource name="products" list={Products} create={CreateProduct} edit={EditProduct} show={DetailProduct} />
                : null,
              role['Users'] !== undefined
                ? <Resource name="users" list={Users} edit={EditUser} create={CreateUser} />
                : null,
              role['Business'] !== undefined
                ? <Resource name="business" list={Business} edit={EditBusiness} create={CreateBusiness} />
                : null,
              role['Branches'] !== undefined
                ? <Resource name="branches" list={Branches} create={CreateBranch} edit={EditBranch} />
                : null,
              role['Terminals'] !== undefined
                ? <Resource name="terminals" list={Terminals} create={CreateTerminal} edit={EditTerminal} />
                : null,
              role['Transactions'] !== undefined
                ? <Resource name="transactions" list={Transactions} show={TransactionDetail} />
                : null,
              role['Orders'] !== undefined
                ? <Resource name="orders" list={Orders} show={OrderDetail} />
                : null,
              role['CustomReport'] !== undefined
                ? <Resource name="reports" list={CustomReport} />
                : null,
              role['SoldProducts'] !== undefined
                ? <Resource name="sold-products" list={SoldProducts} show={SoldProductsDetail} />
                : null,
              role['CloseReports'] !== undefined
                ? <Resource name="close-reports" list={CloseReports} />
                : null,
              role['Settings'] !== undefined
                ? <Resource name="settings" list={Settings} />
                : null,
              role['Processors'] !== undefined
                ? <Resource name="processors" list={Processors} create={CreateProcessor} edit={EditProcessor} />
                : null,
              role['Features'] !== undefined
                ? <Resource name="features" list={Features} create={CreateFeature} edit={EditFeature} />
                : null,
              role['PaymentMethods'] !== undefined
                ? <Resource name="payment_methods" list={PaymentMethods} create={CreatePaymentMethods} edit={EditPaymentMethods} />
                : null,
              role['Banks'] !== undefined
                ? <Resource name="banks" list={Banks} create={CreateBank} edit={EditBank} />
                : null,
              role['Categories'] !== undefined
                ? <Resource name="categories" list={ Categories } create={ CreateCategory } edit={ EditCategory } />
                : null,
              role['Services'] !== undefined
                ? <Resource name="services" list={Services} />
                : null,
              role['ProductBatch'] !== undefined
                ? <Resource name="product-batch" list={ ProductBatch }/>
                : null,
              role['ProductStock'] !== undefined
                ? <Resource name="product-stock" list={ ProductStock }/>
                : null,

              // role['Credentials'] !== undefined
              //   ? <Resource name="processor-credentials" list={ Credentials } create={ CreateCredential } edit={ EditCredential } />
              //   : null,
            ]
          }
        }}
      </Admin>
    </Suspense>

  </CacheBuster>
);

export default App;
